// THE DEAL A PAYOUT SETTLES, OPTIONAL (the wallet's P-063, b8241b2b, 3 Nov 2026): the payee's open trades to pick from, so the Books
// book the payout against its deal instead of it being found by hand. "No deal" is the default and stays a choice. The list is the
// payee's newest trades (api.lastTrades — read only), voided and settled ones left out; the server is the rule (a voided deal, or
// another customer's, is refused with its own line).
import { useQuery } from '@tanstack/react-query';
import { api, type TradeRow } from '../../../lib/api';
import { fmtMoney, fmtStamp } from '../../../lib/format';

/** A trade a payout may settle: not voided, not settled. A row without its lifecycle (an older Worker) is offered. */
export const openForPayout = (t: Pick<TradeRow, 'lifecycle'>): boolean => !['voided', 'settled', 'absorbed'].includes(String(t.lifecycle ?? ''));

/** "TRD-2026-11-02-0001A · 12,500 USDT·TRC · 2 Nov, 10:31": the deal, what the desk gives in it, when it was booked. */
export const dealOption = (t: Pick<TradeRow, 'deal_id' | 'amount_give' | 'asset_give' | 'booked_at'>): string =>
  `${t.deal_id} · ${fmtMoney(String(t.amount_give), t.asset_give)} · ${fmtStamp(t.booked_at)}`;

export function DealField({ party, value, onChange, locked }: { party: string; value: string | null; onChange: (dealId: string | null) => void; locked: boolean }) {
  const q = useQuery({ queryKey: ['trades', 'payout-deals', party], queryFn: () => api.lastTrades('dubaiotc', { party, pageSize: 20 }), staleTime: 30_000 });
  const open = (q.data?.trades ?? []).filter((t) => openForPayout(t) && (!t.party_slug || t.party_slug === party));
  // A deal already chosen stays listed while it is the choice, even if a refresh no longer carries it.
  const shown = value && !open.some((t) => t.deal_id === value) ? [{ deal_id: value, label: value }] : [];
  return (
    <label className="block">
      <span className="mb-1 block text-body font-medium">Deal (optional)</span>
      <select className="select w-full" value={value ?? ''} disabled={locked || (!q.data && !q.isError)} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{!q.data && !q.isError ? 'Reading…' : 'No deal'}</option>
        {shown.map((s) => <option key={s.deal_id} value={s.deal_id}>{s.label}</option>)}
        {open.map((t) => <option key={t.deal_id} value={t.deal_id}>{dealOption(t)}</option>)}
      </select>
      {q.isError && <span className="mt-1 block text-meta text-tg-hint">Couldn&apos;t read the trades — pay with no deal, or try later.</span>}
      {q.data && open.length === 0 && <span className="mt-1 block text-meta text-tg-hint">No open trades for this payee.</span>}
    </label>
  );
}
